import {UncachedEvaluator} from "@cm/graph-v3"
import {Matrix4} from "@cm/math"
import type {CompactUIDTable} from "@cm/utils"
import type {ObjectBounds} from "#template/geometry-processing/bounds"
import {HdriLight} from "#template/nodes/render/hdri-light"
import {chainDescriptors, inputSourceMappingDesc, outputAffectedObjectPathPartsDesc} from "#template/descriptor-chaining"
import type {ObjectId} from "#template/interfaces/scene-object"
import {SceneNodes} from "#template/interfaces/scene-object"
import {NodeEvaluator} from "#template/node-evaluator"
import type {MaterialLikeNode} from "#template/nodes/types"
import {Camera} from "#template/nodes/object/camera"
import type {MaterialAssignments} from "#template/nodes/material-assignment/material-assignments"
import type {MeshDecal} from "#template/nodes/object/mesh-decal"
import {PostProcessRender} from "#template/nodes/render/post-process-render"
import {Render} from "#template/nodes/render/render"
import {SceneProperties} from "#template/nodes/render/scene-properties"
import type {TemplateGraph} from "#template/nodes/template-graph/template-graph"
import type {TemplateInstance} from "#template/nodes/object/template-instance"
import type {BuilderOutlet, GraphBuilder} from "#template/runtime-graph/graph-builder"
import {mergeComponentOutputs} from "#template/output-forwarding"
import {MergeSolverData} from "#template/runtime-graph/nodes/merge-solver-data"
import type {SolverData} from "#template/runtime-graph/nodes/solver/data"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {ExternalId} from "#template/value-types"
import type {LiveValueAccessor, TransformAccessor} from "#template/interactions"
import type {ContextOtherData, TemplateContext} from "#template/compile-context"
import type {EvaluatedTemplateInputs} from "#template/fundamental/evaluated-input"
import {getEmptyCompileContextData} from "#template/compile-context"
import type {ObjectPath, TemplateNodeContext} from "#template/template-node/types"
import type {TemplateNode} from "#template/template-node/template-node"
import {idBelongsToDimensionGuideBox} from "#template/utils/dimension-guide-utils"
import {mergeBounds, transformBounds} from "#template/utils/scene-geometry-utils"

const TD = TypeDescriptors

export interface TransformAccessorListEntry {
    templateNode: TemplateNode
    transformAccessor: TransformAccessor
}

export interface LiveValueAccessorListEntry {
    templateNode: TemplateNode
    accessor: LiveValueAccessor<unknown>
}

const compileTemplateDescriptor = {
    templateContext: TD.inlet(TD.Identity<TemplateContext>()),
    graph: TD.inlet(TD.Nullable(TD.Identity<TemplateGraph>())), //TODO: deepCompare for graph?
    sceneProperties: TD.inlet(TD.Nullable(TD.Identity<SceneProperties>())),
    render: TD.inlet(TD.Nullable(TD.Identity<Render>())),
    environment: TD.inlet(TD.Nullable(TD.Identity<HdriLight>())),
    inputs: TD.inlet(TD.ShallowJSON<EvaluatedTemplateInputs>()),
    path: TD.inlet(TD.Array(TD.Identity<TemplateNode>())),
    compiledTemplateId: TD.inlet(TD.Nullable(TD.Primitive<ObjectId>())),
    templateDepth: TD.inlet(TD.Number),
    exposeClaimedSubTemplateInputs: TD.inlet(TD.Boolean),
    overrideMaterial: TD.inlet(
        TD.Nullable(TD.Function<(parent: MeshDecal | {materialAssignments: MaterialAssignments; key: string}) => MaterialLikeNode | null | undefined>()),
    ),
    contextOtherData: TD.inlet(TD.Nullable(TD.Identity<ContextOtherData>())),
    builder: TD.builder(),
    compiledTemplate: TD.outlet(TD.Function<(instanceToBind: any) => () => void>()),
    activeNodeSet: TD.outlet(TD.Set<TemplateNode>()),
    externalIdToNodeMap: TD.outlet(TD.Map<string, TemplateNode>(TD.Identity())),
}

export class CompileTemplate implements NodeClassImpl<typeof compileTemplateDescriptor, typeof CompileTemplate> {
    static descriptor = compileTemplateDescriptor
    static uniqueName = "CompileTemplate"
    templateContext!: Inlet<TemplateContext>
    graph!: Inlet<TemplateGraph | null>
    sceneProperties!: Inlet<SceneProperties | null>
    camera!: Inlet<Camera | null>
    postProcessRender!: Inlet<PostProcessRender | null>
    environment!: Inlet<HdriLight | null>
    render!: Inlet<Render | null>
    inputs!: Inlet<EvaluatedTemplateInputs>
    path!: Inlet<ObjectPath>
    compiledTemplateId!: Inlet<ObjectId | null>
    templateDepth!: Inlet<number>
    exposeClaimedSubTemplateInputs!: Inlet<boolean>
    overrideMaterial!: Inlet<((parent: MeshDecal | {materialAssignments: MaterialAssignments; key: string}) => MaterialLikeNode | null | undefined) | null>
    contextOtherData!: Inlet<ContextOtherData | null>
    builder!: GraphBuilder
    compiledTemplate!: Outlet<(instanceToBind: any) => () => void>
    activeNodeSet!: Outlet<Set<TemplateNode>>
    externalIdToNodeMap!: Outlet<Map<string, TemplateNode>>

    run() {
        if (this.templateContext === NotReady) return
        if (this.graph === NotReady) return
        if (this.sceneProperties === NotReady) return
        if (this.render === NotReady) return
        if (this.camera === NotReady) return
        if (this.postProcessRender === NotReady) return
        if (this.environment === NotReady) return
        if (this.inputs === NotReady) return
        if (this.path === NotReady) return
        if (this.compiledTemplateId === NotReady) return
        if (this.templateDepth === NotReady) return
        if (this.exposeClaimedSubTemplateInputs === NotReady) return
        if (this.overrideMaterial === NotReady) return
        if (this.contextOtherData === NotReady) return

        if (this.graph === null) throw new Error("Graph is null")

        const {activeNodeSet, localSceneProperties, localRender, localCamera, localPostProcessRender, localEnvironment} = getTemplateProperties(
            this.templateContext.localIds,
            this.builder,
            this.templateContext,
            this.inputs,
            this.graph,
            this.sceneProperties,
            this.render,
            this.camera,
            this.postProcessRender,
            this.environment,
            this.contextOtherData ?? undefined,
        )

        const templateScope = this.builder.scope()

        const context: TemplateNodeContext = {
            type: "onCompile",
            ...getEmptyCompileContextData(
                new NodeEvaluator(this.templateContext.localIds, templateScope, this.templateContext, this.inputs, activeNodeSet),
                this.graph,
                localSceneProperties,
                localRender,
                localCamera,
                localPostProcessRender,
                localEnvironment,
                this.templateDepth,
                this.path,
                this.exposeClaimedSubTemplateInputs,
                this.overrideMaterial ?? undefined,
                this.contextOtherData ?? undefined,
            ),
            skipNode: (node) => !activeNodeSet.has(node),
        }

        const resultCompile = new UncachedEvaluator(context, true)
        resultCompile.getSync(this.graph)

        const {currentTemplate, subTemplates} = context
        const {
            preDisplayList: currentPreDisplayList,
            displayList: currentDisplayList,
            descriptorList: currentDescriptorList,
            infoList: currentInfoList,
            solverData,
            transformAccessorList,
            liveValueAccessorList,
            allBounds,
            templateOutputs,
            outputAffectedObjectPathParts,
            nodeToInfoMap,
        } = currentTemplate
        const {solverObjects, solverVariables, solverRelations} = solverData
        const {
            readyList,
            preDisplayLists,
            displayLists,
            descriptorLists,
            subTemplatesOutputs,
            transformAccessorLists,
            liveValueAccessorLists,
            infoLists,
            solverDatas,
            lookupByExternalIdPathMap,
            inputSourceMappings,
            nodeToInfoMaps,
        } = subTemplates

        const thisPreDisplayList = templateScope.filterInvalid(templateScope.list(currentPreDisplayList, "thisPreDisplayList"))
        const thisDisplayList = templateScope.filterInvalid(templateScope.list(currentDisplayList, "thisDisplayList"))

        const mergedCurrentDisplayLists = templateScope.merge([thisPreDisplayList, thisDisplayList])
        const currentDisplayListsReady = templateScope.phi(
            templateScope.pureLambda(mergedCurrentDisplayLists, () => true, "displayDataReady"),
            false,
        )

        const ready = templateScope.and([...readyList, currentDisplayListsReady])

        templateScope.output("ready", ready)

        const thisPreDisplayListSparse = templateScope.filterInvalid(templateScope.sparseList(currentPreDisplayList, "thisPreDisplayListSparse"))
        const preDisplayList = templateScope.flatten(
            templateScope.sparseMerge([thisPreDisplayListSparse, ...preDisplayLists], "preDisplayListArray"),
            "preDisplayList",
        )
        const filteredPreDisplayList = templateScope.pureLambda(
            preDisplayList,
            (preDisplayList) => {
                //Filter all PreLoadMaterials that are duplicates
                const seenKeys = new Set<string>()
                return preDisplayList.filter((node) => {
                    if (!(SceneNodes.PreloadMaterial.is(node) || SceneNodes.DataObjectReference.is(node))) return true
                    const {id} = node
                    if (seenKeys.has(id)) return false
                    seenKeys.add(id)
                    return true
                })
            },
            "filteredPreDisplayList",
        )

        templateScope.output("preDisplayList", filteredPreDisplayList)

        const displayList = templateScope.flatten(templateScope.merge([thisDisplayList, ...displayLists], "displayListArray"), "displayList")
        templateScope.output("displayList", displayList)

        const taggedInputSourceMappings = inputSourceMappings.map((x, i) =>
            templateScope.pureLambda(
                templateScope.tuple(x.rawDescriptorList, templateScope.value(x.mapping, inputSourceMappingDesc, `inputSourceMapping-${i}`), x.nodeId),
                ([rawDescriptors, mapping, nodeId]) => ({mapping, rawDescriptors, nodeId}),
                `taggedInputSourceMapping-${i}`,
            ),
        )

        const descriptorList = templateScope.pureLambda(
            templateScope.tuple(
                templateScope.merge([templateScope.list(currentDescriptorList, "thisDescriptorList"), ...descriptorLists], "descriptorList"),
                templateScope.list(taggedInputSourceMappings, "taggedInputSourceMappings"),
                templateScope.value(outputAffectedObjectPathParts, outputAffectedObjectPathPartsDesc, "outputAffectedObjectPathParts"),
            ),
            ([descriptorList, taggedMappings, outputAffectedObjectPathParts]) =>
                chainDescriptors(descriptorList, taggedMappings, outputAffectedObjectPathParts),
            "sortedDescriptorList",
        )

        templateScope.output("descriptorList", descriptorList)

        const thisInfoListSparse = templateScope.filterInvalid(templateScope.sparseList(currentInfoList, "thisInfoListSparse"))
        const infoList = templateScope.sparseMerge([thisInfoListSparse, ...infoLists], "infoList")

        templateScope.output("infoList", infoList)

        const currentSolverData = templateScope.struct<SolverData>("SolverData", {
            objects: templateScope.list(solverObjects),
            relations: templateScope.filterInvalid(templateScope.list(solverRelations)),
            variables: templateScope.list(solverVariables),
        })
        templateScope.output(
            "solverData",
            templateScope.node(MergeSolverData, {
                input: templateScope.list([currentSolverData, ...solverDatas]),
            }).output,
        )

        const outputs = mergeComponentOutputs(templateScope, [templateScope.group("outputs", templateOutputs), ...subTemplatesOutputs], "outputs")
        templateScope.output("outputs", outputs)

        const mergedBounds = templateScope.pureLambda(
            templateScope.tuple(templateScope.list(allBounds), this.templateContext.templateMatrix, this.compiledTemplateId),
            ([allBounds, templateMatrix, compiledTemplateId]) => {
                const inverseTemplateMatrix = templateMatrix.inverse()
                const transformedBounds = allBounds.map((x) => transformBounds(x, inverseTemplateMatrix.multiply(Matrix4.fromArray(x.matrix)))) //Dimension guide box bounds are contained here, is this a problem or correct?
                return {...mergeBounds(transformedBounds), objectId: compiledTemplateId ?? "", matrix: templateMatrix.toArray()}
            },
            "mergedBounds",
        )
        templateScope.output("bounds", mergedBounds)

        /*A bounds element has the full transformation matrix chain associated, including the matrix
        of the current template. From a bounding box' point of view, the template itself must be considered as primitive.
        Its untransformed bounds are computed from the true sub-element placements inside the template. The matrix for the bounds
        then is the template's matrix. */
        const {logger} = this.builder
        const mergedDimensionGuideBounds: BuilderOutlet<ObjectBounds> = templateScope.pureLambda(
            // pure-lambda-allow: logger
            templateScope.tuple(templateScope.list(allBounds), this.templateContext.templateMatrix),
            ([allBounds, templateMatrix]) => {
                //Use either all regular bounds or only the ones for dimension guides
                let guideObjectId = ""
                let dimensionGuideBounds = allBounds.filter((x) => {
                    return idBelongsToDimensionGuideBox(x.objectId)
                })
                if (dimensionGuideBounds.length == 0) dimensionGuideBounds = allBounds
                else {
                    guideObjectId = dimensionGuideBounds[0].objectId
                    if (dimensionGuideBounds.length > 1) logger.warn("More than one dimension guide bounds found, using id of the first one")
                }

                const inverseTemplateMatrix = templateMatrix.inverse()
                const boundsOfTemplateElement = dimensionGuideBounds.map((x): ObjectBounds => {
                    const boundsWithoutTemplateMatrix = transformBounds(x, inverseTemplateMatrix.multiply(Matrix4.fromArray(x.matrix)))
                    return {...boundsWithoutTemplateMatrix, objectId: x.objectId, matrix: templateMatrix.toArray()}
                })
                return {...mergeBounds(boundsOfTemplateElement), objectId: guideObjectId, matrix: Matrix4.identity().toArray()}
            },
            "dimensionGuideBounds",
        )
        templateScope.output("dimensionGuideBounds", mergedDimensionGuideBounds)

        templateScope.output(
            "transformAccessorList",
            templateScope.merge([templateScope.list(transformAccessorList, "transformAccessorList"), ...transformAccessorLists], "transformAccessorList"),
        )
        templateScope.output(
            "liveValueAccessorList",
            templateScope.merge([templateScope.list(liveValueAccessorList, "liveValueAccessorList"), ...liveValueAccessorLists], "liveValueAccessorList"),
        )

        const externalIdToNodeMap = getExternalIdToNodeMap(activeNodeSet)
        const childLookupFnKeys = [...lookupByExternalIdPathMap.keys()]
        const lookupByExternalIdPath = templateScope.pureLambda(
            templateScope.tuple(childLookupFnKeys, templateScope.list([...lookupByExternalIdPathMap.values()]), externalIdToNodeMap),
            ([childLookupFnKeys, childLookupFns, externalIdToNodeMap]) => {
                return (path: string[]) => {
                    const shiftedPath = path.shift()
                    if (shiftedPath === undefined) {
                        return null
                    } else {
                        const childNode = externalIdToNodeMap.get(shiftedPath)
                        if (!childNode) return null
                        else if (path.length === 1) return childNode
                        const idx = childLookupFnKeys.indexOf(childNode as TemplateInstance)
                        if (idx < 0) return null
                        return childLookupFns[idx](path)
                    }
                }
            },
            "lookupByExternalIdPath",
        )
        templateScope.output("lookupByExternalIdPath", lookupByExternalIdPath)

        const mergedNodeToInfoMap = templateScope.pureLambda(
            templateScope.tuple(templateScope.merge(nodeToInfoMaps), nodeToInfoMap),
            ([subMaps, nodeToInfoMap]) => {
                const merged = new Map(nodeToInfoMap)
                for (const subMap of subMaps) {
                    if (subMap === null) continue
                    for (const [key, value] of subMap) {
                        merged.set(key, value)
                    }
                }
                return merged
            },
            "mergedNodeToInfoMap",
        )
        templateScope.output("nodeToInfoMap", mergedNodeToInfoMap)

        const compiledTemplate = this.builder.finalizeChanges()

        this.compiledTemplate.emit(compiledTemplate)
        this.activeNodeSet.emit(activeNodeSet)
        this.externalIdToNodeMap.emit(externalIdToNodeMap)
    }
}

const getTemplateProperties = (
    idMap: CompactUIDTable<string>,
    graphBuilder: GraphBuilder,
    templateContext: TemplateContext,
    inputs: EvaluatedTemplateInputs,
    graph: TemplateGraph,
    sceneProperties: SceneProperties | null,
    render: Render | null,
    camera: Camera | null,
    postProcessRender: PostProcessRender | null,
    environment: HdriLight | null,
    contextOtherData?: ContextOtherData,
) => {
    const activeNodeSet = new Set<TemplateNode>()

    let localSceneProperties: SceneProperties | undefined = sceneProperties ?? undefined
    let localRender: Render | undefined = render ?? undefined
    let localCamera: Camera | undefined = camera ?? undefined
    let localPostProcessRender: PostProcessRender | undefined = postProcessRender ?? undefined
    let localEnvironment: HdriLight | undefined = environment ?? undefined

    const skipNode = (node: TemplateNode) => {
        if (node instanceof SceneProperties) {
            localSceneProperties = localSceneProperties ?? node
            return localSceneProperties !== node
        } else if (node instanceof Render) {
            localRender = localRender ?? node
            return localRender !== node
        } else if (node instanceof Camera) {
            if (node.parameters.visible) {
                localCamera = localCamera ?? node
                return localCamera !== node
            } else return true
        } else if (node instanceof PostProcessRender) {
            localPostProcessRender = localPostProcessRender ?? node
            return localPostProcessRender !== node
        } else if (node instanceof HdriLight) {
            localEnvironment = localEnvironment ?? node
            return localEnvironment !== node
        }

        return false
    }

    //Perform a first pass to determine which nodes are active based on the default selections
    {
        const preEvaluator = new NodeEvaluator(idMap, graphBuilder.scope(), templateContext, inputs, undefined)
        const preContext: TemplateNodeContext = {
            type: "onFilterActive",
            evaluator: preEvaluator,
            root: graph,
            otherData: contextOtherData,
            skipNode,
            onPostUnskippedNode: (node) => {
                const preSize = activeNodeSet.size
                activeNodeSet.add(node)
                if (activeNodeSet.size === preSize) throw new Error(`Node ${node} is already in activeNodeSet`)
            },
        }

        const preResultFilterActive = new UncachedEvaluator(preContext, true)
        preResultFilterActive.getSync(graph)
    }

    //Perform a second round of passes to add nodes as active that might only conditionally become active based on the activeNodeSet
    {
        const postEvaluator = new NodeEvaluator(idMap, graphBuilder.scope(), templateContext, inputs, activeNodeSet)

        let newPassRequired = true
        while (newPassRequired) {
            newPassRequired = false

            const postContext: TemplateNodeContext = {
                type: "onFilterActive",
                evaluator: postEvaluator,
                root: graph,
                otherData: contextOtherData,
                skipNode,
                onPostUnskippedNode: (node) => {
                    if (!activeNodeSet.has(node)) newPassRequired = true

                    activeNodeSet.add(node)
                },
            }

            const postResultFilterActive = new UncachedEvaluator(postContext, true)
            postResultFilterActive.getSync(graph)
        }
    }

    return {activeNodeSet, localSceneProperties, localRender, localCamera, localPostProcessRender, localEnvironment}
}

const getExternalIdToNodeMap = (activeNodeSet: Set<TemplateNode>) => {
    const externalIdToNodeMap = new Map<string, TemplateNode>()
    const hasId = (node: TemplateNode): node is TemplateNode<{id: ExternalId}> => {
        const {id} = node as {id?: ExternalId}
        return typeof id === "string"
    }
    for (const node of activeNodeSet) if (hasId(node)) externalIdToNodeMap.set(node.parameters.id, node)
    return externalIdToNodeMap
}
